import React, { useCallback, useMemo, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { useDocumentaryScreeningMockRun } from "../hooks/useDocumentaryScreeningMockRun"
import {
  PIPELINE_STEPS,
  PHASE1_WEAK_AXIS_MESSAGE_EN,
  PHASE1_WEAK_AXIS_MESSAGE_FR,
} from "../mocks/documentaryScreeningFixtures"

const TAB_IDS = /** @type {const} */ ([
  "axes",
  "log",
  "coverage",
  "index",
  "inaccessible",
])

const TAB_LABELS = {
  axes: { fr: "Axes de recherche", en: "Research axes" },
  log: { fr: "Journal des recherches", en: "Search log" },
  coverage: { fr: "Matrice de couverture", en: "Coverage matrix" },
  index: { fr: "INDEX (aperçu)", en: "INDEX (preview)" },
  inaccessible: { fr: "Sources inaccessibles", en: "Inaccessible sources" },
}

const DEFAULT_FORM = {
  topic: "",
  audience: "",
  country: "",
  outline: "",
  constraints: "",
}

function formatElapsed(ms) {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const r = s % 60
  return m > 0 ? `${m}m ${r}s` : `${r}s`
}

/** Side-by-side raw Markdown vs rendered HTML so Nex-Com-style deliverables are easy to compare. */
function MarkdownSourceAndPreview({ markdown }) {
  const box =
    "max-h-[28rem] min-h-[8rem] overflow-y-auto rounded border border-gray-100"
  return (
    <div className="grid gap-3 md:grid-cols-2 md:items-start">
      <div className="min-w-0">
        <p className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">
          Source (Markdown)
          <span className="ml-1 font-normal text-gray-500">/ Raw</span>
        </p>
        <pre
          className={`${box} whitespace-pre-wrap break-words bg-gray-50 dark:bg-gray-950 dark:bg-gray-950/90 p-3 text-xs font-mono text-gray-800 sm:text-sm`}
        >
          {markdown}
        </pre>
      </div>
      <div className="min-w-0">
        <p className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">
          Aperçu rendu
          <span className="ml-1 font-normal text-gray-500">/ Rendered</span>
        </p>
        <div
          className={`${box} bg-white dark:bg-gray-900 p-3 prose prose-sm max-w-none text-gray-900 dark:text-gray-100 prose-headings:text-gray-900 dark:text-gray-100 prose-p:text-gray-800 prose-table:text-xs prose-th:border prose-th:border-gray-200 prose-th:px-2 prose-th:py-1 prose-td:border prose-td:border-gray-200 prose-td:px-2 prose-td:py-1`}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
        </div>
      </div>
    </div>
  )
}

function FieldLabel({ fr, en, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-800">
      <span className="block">{fr}</span>
      <span className="block text-xs font-normal text-gray-500">{en}</span>
    </label>
  )
}

export default function DocumentaryScreeningMock({
  stepDurationMs = 1800,
  showMockBanner = true,
  /** Pre-select mode in Storybook without freezing (omit `storybookSnapshot`). */
  storybookInitialMode = null,
  /** Frozen run state for Storybook (phase, result, progress). */
  storybookSnapshot = null,
  /** Prefill the five fields when using snapshots or mode demo. */
  storybookSampleForm = null,
  /** Short note under the banner (e.g. what this story shows). */
  storybookCaption = null,
}) {
  const [mode, setMode] = useState(() => /** @type {'single' | 'two_phase'} */ (storybookInitialMode ?? "single"))
  const [form, setForm] = useState(() => ({
    ...DEFAULT_FORM,
    ...(storybookSampleForm && typeof storybookSampleForm === "object" ? storybookSampleForm : {}),
  }))
  const [tab, setTab] = useState(/** @type {(typeof TAB_IDS)[number]} */ ("axes"))
  const [copyFeedback, setCopyFeedback] = useState("")

  const {
    phase,
    activeStepIndex,
    elapsedMs,
    result,
    isRunning,
    startPhase1,
    startPhase2,
    reset,
    exportPayload,
  } = useDocumentaryScreeningMockRun({ mode, stepDurationMs, storySnapshot: storybookSnapshot })

  const formValid = useMemo(
    () =>
      ["topic", "audience", "country", "outline", "constraints"].every(
        (k) => String(form[k]).trim().length > 0
      ),
    [form]
  )

  const onField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }, [])

  const onModeChange = useCallback(
    (next) => {
      if (isRunning || storybookSnapshot) return
      setMode(next)
      reset()
    },
    [isRunning, reset, storybookSnapshot]
  )

  const onCopyIndex = useCallback(async () => {
    if (!result?.indexMarkdown) return
    try {
      await navigator.clipboard.writeText(result.indexMarkdown)
      setCopyFeedback("INDEX copié / copied")
      setTimeout(() => setCopyFeedback(""), 2500)
    } catch {
      setCopyFeedback("Copie impossible / copy failed")
      setTimeout(() => setCopyFeedback(""), 2500)
    }
  }, [result])

  const onExportJson = useCallback(() => {
    const payload = exportPayload(form)
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "documentary_screening_mock_export.json"
    a.click()
    URL.revokeObjectURL(url)
  }, [exportPayload, form])

  const showResults = result != null
  const showPhase2Cta = mode === "two_phase" && phase === "phase1_done" && result

  return (
    <div
      id="documentary-screening-mock-root"
      className="documentary-screening-mock w-full max-w-5xl space-y-6"
    >
      {showMockBanner && (
        <div
          id="documentary-screening-mock-banner"
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <strong className="font-semibold">Mock interne</strong> — aucun appel réel au web / LLM.
          <span className="ml-1 text-amber-800">
            Internal mock — no live web or LLM calls.
          </span>
        </div>
      )}
      {storybookCaption ? (
        <p
          id="documentary-screening-storybook-caption"
          className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700"
        >
          {storybookCaption}
        </p>
      ) : null}

      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Screening documentaire (RP médicale)
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Documentary screening for medical professional meetings — Nex-Com methodology (simulation).
        </p>
      </header>

      <section
        id="documentary-screening-mode"
        className="rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-950 dark:bg-gray-950 p-4"
        aria-label="Mode de parcours / Flow mode"
      >
        <p className="text-sm font-medium text-gray-800 mb-2">
          Mode de parcours <span className="text-gray-500 font-normal">/ Flow mode</span>
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="documentary-screening-mode"
              className="mt-1"
              checked={mode === "single"}
              disabled={isRunning || !!storybookSnapshot}
              onChange={() => onModeChange("single")}
            />
            <span>
              <span className="font-medium text-gray-900 dark:text-gray-100">Parcours unique</span>
              <span className="block text-xs text-gray-600 dark:text-gray-400">Single run — full deliverables at the end</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="documentary-screening-mode"
              className="mt-1"
              checked={mode === "two_phase"}
              disabled={isRunning || !!storybookSnapshot}
              onChange={() => onModeChange("two_phase")}
            />
            <span>
              <span className="font-medium text-gray-900 dark:text-gray-100">Deux phases (README)</span>
              <span className="block text-xs text-gray-600 dark:text-gray-400">
                Phase 1 stop, then “Passe 2 — couverture & snowballing”
              </span>
            </span>
          </label>
        </div>
      </section>

      <section id="documentary-screening-form" className="space-y-4">
        <div>
          <FieldLabel
            fr="Sujet de la RP"
            en="Meeting / RP topic"
            htmlFor="documentary-screening-topic"
          />
          <input
            id="documentary-screening-topic"
            type="text"
            disabled={isRunning}
            value={form.topic}
            onChange={(e) => onField("topic", e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900"
            placeholder="Ex. Maladies cardiovasculaires chez la femme"
          />
        </div>
        <div>
          <FieldLabel
            fr="Public cible"
            en="Target audience"
            htmlFor="documentary-screening-audience"
          />
          <input
            id="documentary-screening-audience"
            type="text"
            disabled={isRunning}
            value={form.audience}
            onChange={(e) => onField("audience", e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900"
            placeholder="Ex. MG et cardiologues"
          />
        </div>
        <div>
          <FieldLabel
            fr="Pays / contexte national"
            en="Country / national context"
            htmlFor="documentary-screening-country"
          />
          <input
            id="documentary-screening-country"
            type="text"
            disabled={isRunning}
            value={form.country}
            onChange={(e) => onField("country", e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900"
            placeholder="Ex. France"
          />
        </div>
        <div>
          <FieldLabel
            fr="Plan thématique"
            en="Thematic outline"
            htmlFor="documentary-screening-outline"
          />
          <textarea
            id="documentary-screening-outline"
            rows={5}
            disabled={isRunning}
            value={form.outline}
            onChange={(e) => onField("outline", e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900"
            placeholder="Coller le sommaire du brief (sections / sous-sections)"
          />
        </div>
        <div>
          <FieldLabel
            fr="Contraintes"
            en="Constraints"
            htmlFor="documentary-screening-constraints"
          />
          <textarea
            id="documentary-screening-constraints"
            rows={3}
            disabled={isRunning}
            value={form.constraints}
            onChange={(e) => onField("constraints", e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 dark:bg-gray-800 dark:bg-gray-900"
            placeholder='Ex. sources < 5 ans, focus Europe, ou "aucune"'
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            id="documentary-screening-run-phase1"
            disabled={!formValid || isRunning}
            onClick={startPhase1}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {mode === "single" ? "Lancer le screening" : "Lancer la phase 1"}
            <span className="ml-2 text-indigo-100 font-normal">
              {mode === "single" ? "/ Run screening" : "/ Start phase 1"}
            </span>
          </button>
          {showPhase2Cta && (
            <button
              type="button"
              id="documentary-screening-run-phase2"
              disabled={isRunning}
              onClick={startPhase2}
              className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              Passe 2 — couverture & snowballing
              <span className="ml-2 text-teal-100 font-normal">/ Phase 2</span>
            </button>
          )}
          <button
            type="button"
            id="documentary-screening-reset"
            disabled={isRunning}
            onClick={reset}
            className="rounded-md border border-gray-300 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:bg-gray-950 dark:bg-gray-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Réinitialiser / Reset
          </button>
        </div>
        {!formValid && (
          <p className="text-xs text-gray-500">
            Remplir les cinq champs pour activer le lancement. / Fill all five fields to enable run.
          </p>
        )}
      </section>

      {(isRunning || (showResults && phase !== "idle")) && (
        <section
          id="documentary-screening-progress"
          className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-4"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-medium text-indigo-950">
              {phase === "phase1_running" && "Phase 1 en cours… / Phase 1 running…"}
              {phase === "phase2_running" && "Passe 2 en cours… / Phase 2 running…"}
              {phase === "complete" && "Terminé / Complete"}
              {phase === "phase1_done" && "Phase 1 terminée — revue / Phase 1 done — review"}
            </p>
            <p className="text-xs text-indigo-800 tabular-nums">
              Temps écoulé / Elapsed: {formatElapsed(elapsedMs)}
            </p>
          </div>
          <ol className="mt-3 space-y-1.5">
            {PIPELINE_STEPS.map((step, idx) => {
              const allStepsDone = activeStepIndex >= PIPELINE_STEPS.length
              const isDone = idx < activeStepIndex || (!isRunning && allStepsDone)
              const isCurrent = isRunning && idx === activeStepIndex
              const state = isCurrent ? "current" : isDone ? "done" : "pending"
              return (
                <li
                  key={step.key}
                  className={`flex gap-2 text-sm ${
                    state === "current"
                      ? "font-semibold text-indigo-900"
                      : state === "done"
                        ? "text-gray-700 dark:text-gray-300"
                        : "text-gray-400"
                  }`}
                >
                  <span className="w-5 shrink-0 text-center" aria-hidden>
                    {state === "done" ? "✓" : state === "current" ? "→" : "○"}
                  </span>
                  <span>
                    {step.labelFr}
                    <span className="block text-xs font-normal text-gray-500">{step.labelEn}</span>
                  </span>
                </li>
              )
            })}
          </ol>
        </section>
      )}

      {showPhase2Cta && (
        <div
          id="documentary-screening-phase1-notice"
          className="rounded-md border border-teal-200 bg-teal-50 px-3 py-3 text-sm text-teal-950"
        >
          <p className="font-medium">Passe 2 recommandée / Second pass recommended</p>
          <p className="mt-1">{PHASE1_WEAK_AXIS_MESSAGE_FR}</p>
          <p className="mt-1 text-teal-900">{PHASE1_WEAK_AXIS_MESSAGE_EN}</p>
        </div>
      )}

      {showResults && (
        <section id="documentary-screening-results" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-2">
            {TAB_IDS.map((id) => (
              <button
                key={id}
                type="button"
                id={`documentary-screening-tab-${id}`}
                onClick={() => setTab(id)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium sm:text-sm ${
                  tab === id
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 dark:bg-gray-800 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:bg-gray-800"
                }`}
              >
                <span className="block sm:hidden">
                  <span className="font-medium">{TAB_LABELS[id].fr}</span>
                  <span className="mt-0.5 block text-[10px] font-normal text-gray-500">
                    {TAB_LABELS[id].en}
                  </span>
                </span>
                <span className="hidden sm:inline">
                  {TAB_LABELS[id].fr}
                  <span className="font-normal text-gray-500"> — {TAB_LABELS[id].en}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              id="documentary-screening-copy-index"
              onClick={onCopyIndex}
              className="rounded-md border border-gray-300 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 dark:bg-gray-950 dark:bg-gray-950"
            >
              Copier INDEX (Markdown) / Copy INDEX
            </button>
            <button
              type="button"
              id="documentary-screening-export-json"
              onClick={onExportJson}
              className="rounded-md border border-gray-300 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 dark:bg-gray-950 dark:bg-gray-950"
            >
              Exporter JSON / Export JSON
            </button>
            {copyFeedback && (
              <span className="self-center text-xs text-gray-600 dark:text-gray-400">{copyFeedback}</span>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white dark:bg-gray-900 p-3 shadow-sm min-h-[12rem]">
            {tab === "axes" && <MarkdownSourceAndPreview markdown={result.axesMarkdown} />}
            {tab === "log" && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600 dark:text-gray-400">
                      <th className="py-2 pr-3 font-medium">Base</th>
                      <th className="py-2 pr-3 font-medium">Requête / Query</th>
                      <th className="py-2 pr-2 font-medium text-right">Bruts / Raw</th>
                      <th className="py-2 font-medium text-right">Retenus / Kept</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.searchLog.map((row) => (
                      <tr key={row.id} className="border-b border-gray-100">
                        <td className="py-2 pr-3 align-top text-gray-900 dark:text-gray-100">{row.base}</td>
                        <td className="py-2 pr-3 align-top font-mono text-gray-700 dark:text-gray-300">{row.query}</td>
                        <td className="py-2 pr-2 align-top text-right tabular-nums">{row.raw}</td>
                        <td className="py-2 align-top text-right tabular-nums font-medium">{row.kept}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tab === "coverage" && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600 dark:text-gray-400">
                      <th className="py-2 pr-3 font-medium">Axe / Axis</th>
                      <th className="py-2 pr-2 font-medium text-right">Tél. / DL</th>
                      <th className="py-2 pr-2 font-medium text-right">Inacc.</th>
                      <th className="py-2 pr-2 font-medium text-right">Total</th>
                      <th className="py-2 font-medium">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.coverage.map((row) => (
                      <tr key={row.axisId} className="border-b border-gray-100">
                        <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">{row.axisLabel}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">{row.downloadable}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">{row.inaccessible}</td>
                        <td className="py-2 pr-2 text-right tabular-nums">{row.total}</td>
                        <td className="py-2">
                          <span
                            className={
                              row.status === "OK"
                                ? "rounded bg-green-100 px-2 py-0.5 text-green-800"
                                : "rounded bg-amber-100 px-2 py-0.5 text-amber-900"
                            }
                          >
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {tab === "index" && <MarkdownSourceAndPreview markdown={result.indexMarkdown} />}
            {tab === "inaccessible" && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600 dark:text-gray-400">
                      <th className="py-2 pr-3 font-medium">Référence</th>
                      <th className="py-2 pr-3 font-medium">Journal</th>
                      <th className="py-2 pr-3 font-medium">Raison</th>
                      <th className="py-2 pr-3 font-medium">Couvert par</th>
                      <th className="py-2 font-medium">Lacune</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.inaccessible.map((row) => (
                      <tr key={row.id} className="border-b border-gray-100">
                        <td className="py-2 pr-3 align-top text-gray-800">{row.ref}</td>
                        <td className="py-2 pr-3 align-top">{row.journal}</td>
                        <td className="py-2 pr-3 align-top">{row.reason}</td>
                        <td className="py-2 pr-3 align-top text-gray-700 dark:text-gray-300">{row.coveredBy}</td>
                        <td className="py-2 align-top">{row.lacune}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  )
}
